import { useEffect, useRef, useState } from "react";
import { point } from "@turf/helpers";
import distance from "@turf/distance";

const pad = (n) => String(n).padStart(2, "0");

function formatDate(date) {
  return `${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}`;
}

function formatTime(date) {
  if (!date) return "";
  return new Date(date).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

const CACHE_TTL = 30 * 60 * 1000; // 30 minutes

function readCache(key) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function writeCache(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    // ignore
  }
}

export default function useTideStage({ lat, lng, enabled = true, stationId: requestedStationId } = {}) {
  const [tideStage, setTideStage] = useState(null);
  const [stationName, setStationName] = useState(null);
  const [stationId, setStationId] = useState(null);
  const [stations, setStations] = useState([]);
  const stationsRef = useRef([]);
  const lastLocationRef = useRef({ lat: null, lng: null });
  const retryTimeoutRef = useRef(null);
  const forceRefreshRef = useRef(false);
  const [nextTide, setNextTide] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [refreshToggle, setRefreshToggle] = useState(0);

  // Keep a ref copy of stations so the main effect doesn't need to depend on it.
  useEffect(() => {
    stationsRef.current = stations;
  }, [stations]);

  const refresh = () => {
    forceRefreshRef.current = true;
    setRefreshToggle((c) => c + 1);
  };


  useEffect(() => {
    if (!enabled) {
      setTideStage(null);
      setStationName(null);
      setStationId(null);
      setError(null);
      setLoading(false);
      return;
    }

    if (lat == null || lng == null || isNaN(lat) || isNaN(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
      setTideStage(null);
      setStationName(null);
      setStationId(null);
      setError("Invalid location coordinates");
      setLoading(false);
      return;
    }

    if (process.env.NODE_ENV === 'development') console.log("useTideStage enabled with lat:", lat, "lng:", lng, "requestedStationId:", requestedStationId);

    let cancelled = false;
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 second timeout

    const MIN_STATION_MOVE_MILES = 0.2;

    const fetchTideStage = async () => {
      setLoading(true);
      setError(null);
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;

      try {
        const loadStations = async (radius) => {
          if (process.env.NODE_ENV === 'development') console.log("Fetching stations for", lat, lng, "radius", radius);
          const stationRes = await fetch(
            `https://api.tidesandcurrents.noaa.gov/mdapi/prod/webapi/stations.json?lat=${lat}&lon=${lng}&radius=${radius}&units=english&format=json`,
            { signal: controller.signal }
          );
          if (!stationRes.ok) {
            throw new Error(`Station API failed: ${stationRes.status} ${stationRes.statusText}`);
          }
          const stationData = await stationRes.json();
          if (process.env.NODE_ENV === 'development') console.log("Station data:", stationData);
          return Array.isArray(stationData?.stations) ? stationData.stations : [];
        };

        const lastLoc = lastLocationRef.current;
        const currentPoint = point([lng, lat]);
        const lastPoint = lastLoc.lat != null ? point([lastLoc.lng, lastLoc.lat]) : null;
        const movedMiles = lastPoint ? distance(lastPoint, currentPoint, { units: "miles" }) : Infinity;

        // If we haven't fetched stations yet, or we've moved a meaningful distance, refresh the station list.
        const shouldFetchStations = !stationsRef.current.length || movedMiles > MIN_STATION_MOVE_MILES;

        let rawStations = stationsRef.current;

        if (shouldFetchStations) {
          rawStations = await loadStations(50);
          if (!rawStations.length) rawStations = await loadStations(250);
          if (!rawStations.length) rawStations = await loadStations(1000);
          if (process.env.NODE_ENV === 'development') console.log("Final station list (count):", rawStations.length);

          // Keep the list around for minor GPS jitter so we don't immediately fall back.
          setStations(rawStations);
          lastLocationRef.current = { lat, lng };
        }

        const userPoint = point([lng, lat]);
        const scored = rawStations
          .filter((s) => s && s.lat != null && s.lng != null)
          .map((s) => {
            const latNum = Number(s.lat);
            const lngNum = Number(s.lng);
            const stationPoint = point([lngNum, latNum]);
            const geoDist = distance(userPoint, stationPoint, { units: "miles" });
            const noaaDist = Number(s.distance ?? Infinity);
            const score = Math.min(geoDist, noaaDist);
            return {
              id: s.id,
              name: s.name,
              lat: latNum,
              lng: lngNum,
              geoDist,
              noaaDist,
              score,
            };
          })
          .sort((a, b) => a.score - b.score);

        setStations(scored);

        const chosen =
          scored.find((s) => s.id === requestedStationId) ||
          scored[0] ||
          null;

        if (!chosen) {
          const msg =
            "No nearby tide station found within 1000 miles. Retrying in 5s...";
          if (!cancelled) {
            setError(msg);
            setLoading(false);
          }

          // Retry once after a short delay to handle temporary GPS jitter / API hiccups.
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = setTimeout(() => {
            if (!cancelled) fetchTideStage();
          }, 5000);

          return;
        }

        const stationId = chosen.id;
        const stationName = chosen.name || stationId;

        const now = new Date();
        const begin = formatDate(now);
        const end = formatDate(new Date(now.getTime() + 24 * 60 * 60 * 1000));
        const cacheKey = `the99:tide:${stationId}:${begin}`;

        const cached = readCache(cacheKey);
        if (!forceRefresh && cached && Date.now() - cached.fetchedAt < CACHE_TTL) {
          if (!cancelled) {
            setTideStage(cached.stage);
            setStationName(cached.stationName);
            setStationId(stationId);
            setNextTide(cached.nextTide ?? null);
            setLoading(false);
          }
          return;
        }

        const predRes = await fetch(
          `https://api.tidesandcurrents.noaa.gov/api/prod/datagetter?product=predictions&application=the99&begin_date=${begin}&end_date=${end}&datum=MLLW&station=${stationId}&units=english&time_zone=lst_ldt&interval=h&format=json`,
          { signal: controller.signal }
        );
        if (!predRes.ok) {
          throw new Error(`Predictions API failed: ${predRes.status} ${predRes.statusText}`);
        }
        const predJson = await predRes.json();
        if (process.env.NODE_ENV === 'development') console.log("Predictions data:", predJson);
        const preds = predJson?.predictions;
        if (!Array.isArray(preds) || preds.length === 0) {
          throw new Error("No tide predictions available");
        }

        const nowTs = now.getTime();
        let closestIdx = 0;
        let closestDiff = Infinity;
        preds.forEach((p, i) => {
          const t = Date.parse(p.t);
          const diff = Math.abs(t - nowTs);
          if (diff < closestDiff) {
            closestDiff = diff;
            closestIdx = i;
          }
        });

        const current = preds[closestIdx];
        const next = preds[closestIdx + 1] || current;

        const currentVal = parseFloat(current.v);
        const nextVal = parseFloat(next.v);

        const trend = nextVal > currentVal ? "Rising" : nextVal < currentVal ? "Falling" : "Stable";

        let nextTide = null;
        for (let i = closestIdx + 1; i < preds.length - 1; i += 1) {
          const a = parseFloat(preds[i - 1].v);
          const b = parseFloat(preds[i].v);
          const c = parseFloat(preds[i + 1].v);
          const isHigh = b > a && b > c;
          const isLow = b < a && b < c;
          if (isHigh || isLow) {
            const dt = Date.parse(preds[i].t);
            if (dt > nowTs) {
              nextTide = {
                type: isHigh ? "High" : "Low",
                time: dt,
                label: `${isHigh ? "High" : "Low"} @ ${formatTime(dt)}`,
              };
              break;
            }
          }
        }

        const stage = `${trend}${nextTide ? ` · ${nextTide.label}` : ""}`;

        const payload = {
          fetchedAt: Date.now(),
          stage,
          stationName,
          nextTide,
        };
        writeCache(cacheKey, payload);

        if (!cancelled) {
          setTideStage(stage);
          setStationName(stationName);
          setStationId(stationId);
          setNextTide(nextTide);
        }
      } catch (err) {
        if (process.env.NODE_ENV === 'development') console.error("Tide fetch error:", err);
        let errorMessage = err?.message || "Failed to load tide data";
        if (err.name === "AbortError") {
          errorMessage = "Request timed out - check network connection";
        }
        if (!cancelled) {
          setError(errorMessage);
          setTideStage(null);
          setNextTide(null);
          setStations([]); // Ensure stations is set even on error
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTideStage();

    const interval = setInterval(fetchTideStage, 30 * 60 * 1000);

    return () => {
      cancelled = true;
      controller.abort();
      clearTimeout(timeoutId);
      clearTimeout(retryTimeoutRef.current);
      clearInterval(interval);
    };
  }, [lat, lng, enabled, refreshToggle, requestedStationId]);

  return { tideStage, stationName, stationId, stations, nextTide, loading, error, refresh };
}
